// ReviewFlagsBanner (2026-11-04, Lyndon): an on-screen notice for scheduling, on every page, whenever a tech has flagged a
// ticket for review. It stays up until each flag is resolved. The ticket stays with the tech until a scheduler changes it.
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, TextField, Typography } from '@mui/material';
import api from '@/services/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const OFFICE = ['SuperAdmin', 'Admin', 'Dispatcher', 'Scheduler'];
const when = (iso?: string | null) => (iso ? new Date(`${iso}Z`).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not scheduled');

export default function ReviewFlagsBanner() {
  const { ws } = useWorkspace() as any;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [flags, setFlags] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const active = ws?.active_role?.name;
  const office = !active || OFFICE.includes(active);

  const load = useCallback(() => {
    if (!office) { setFlags([]); return; }
    api.get('/tech-app/review-flags').then((r: any) => setFlags(Array.isArray(r.data) ? r.data : [])).catch(() => setFlags([]));
  }, [office]);
  useEffect(() => { load(); const h = setInterval(load, 30000); return () => clearInterval(h); }, [load]);
  useEffect(() => { if (open && !flags.length) setOpen(false); }, [open, flags.length]);

  const resolve = async (id: number) => {
    setError('');
    try { await api.post(`/tech-app/review-flags/${id}/resolve`, { note: notes[id] || null }); load(); }
    catch { setError('That did not save. Try again.'); }
  };
  const openTicket = (id: number) => { setOpen(false); navigate(`${pathname.startsWith('/tech') ? '/tech/tickets' : '/tickets'}?id=${id}`); };

  if (!flags.length) return null;
  return (
    <>
      <Alert severity="warning" variant="filled" sx={{ mb: 2 }}
        action={<Button color="inherit" size="small" onClick={() => setOpen(true)}>Review</Button>}>
        {flags.length} ticket{flags.length === 1 ? '' : 's'} flagged by a tech for scheduling review
      </Alert>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Tickets flagged for review</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These stay with the tech and on their schedule until you change them.
          </Typography>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {flags.map((f, i) => (
            <Box key={f.id}>
              {i > 0 ? <Divider sx={{ my: 2 }} /> : null}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700 }}>{f.ticket_number}</Typography>
                <Chip size="small" color="warning" label={f.reason} />
              </Box>
              <Typography variant="body2">{[f.customer_name, f.service_city, f.service_state].filter(Boolean).join(' · ')}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Flagged by {f.flagged_by} · {when(f.created_at)} · Scheduled {when(f.scheduled_start)}
              </Typography>
              {f.note ? <Typography variant="body2" sx={{ mt: 1 }}>{f.note}</Typography> : null}
              <TextField size="small" fullWidth sx={{ mt: 1.5 }} label="Note back to the tech (optional)"
                value={notes[f.id] || ''} onChange={(e) => setNotes({ ...notes, [f.id]: e.target.value })} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => openTicket(f.ticket_id)}>Open ticket</Button>
                <Button size="small" variant="contained" onClick={() => resolve(f.id)}>Mark reviewed</Button>
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
}
